import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { toJS } from 'mobx'
import moment from 'moment'
import Button from '@components/Button'
import FormLoader from '@components/FormLoader'
import PropTypes from 'prop-types'
import FormError from '@components/FormError'
import { Formik, Form, Field, FieldArray } from 'formik'
import Input from '@components/Form/Input'
import DatePicker from '@components/Form/DatePicker'
import { Dropdown } from '@components/Form/Autocomplete'
import RepeatableAction from '@components/Form/RepeatableAction'
import yup from 'yup'
import { invoiceApi } from '@atheneum/api'
import { FlexItem, FlexRow } from '@components/Flex'
import InvoiceOverpaidNotification from '../InvoiceOverpaidNotification'
import { moneyValidate } from '@atheneum/api/dist/lib'

class InvoicePaymentForm extends Component {
  static propTypes = {
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    title: PropTypes.string,
    invoice: PropTypes.object,
    growlStore: PropTypes.object,
    invoiceStore: PropTypes.object
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: true
  }

  state = {
    error: null,
    loading: false
  }

  handleInvoicePaymentStatusChange = (
    form,
    fieldName,
    invoicePaymentStatus
  ) => {
    form.setFieldValue(fieldName, invoicePaymentStatus, false)
    form.setFieldTouched(fieldName, true, true)

    if (!invoicePaymentStatus) return
    form.setFieldValue(`${fieldName}Id`, invoicePaymentStatus.id, false)
  }

  submitForm = async (values, actions) => {
    const { invoice, modalStore, growlStore, invoiceStore } = this.props

    try {
      this.setState({
        error: null,
        loading: true
      })

      await invoiceApi.updatePayments(invoice.id, values.invoicePayments)

      invoiceStore.get(invoice.id)

      growlStore.showSuccess(`Updated payments for invoice ${invoice.name}`)
      modalStore.close()
    } catch (error) {
      actions.setSubmitting(false)
      growlStore.showError(error)
      this.setState({
        error,
        loading: false
      })
    }
  }

  render () {
    const { title, modalStore, staticData, invoice } = this.props
    const currencyNames = staticData.names.currencies
    const currencyName = (invoice && currencyNames[invoice.currencyId]) || 'N/A'

    return (
      <Formik
        initialValues={initializeForm({ invoice, staticData })}
        validationSchema={getValidationSchema({ staticData })}
        onSubmit={this.submitForm}
        render={form => {
          return (
            <div className='expert-form invoice-form'>
              <Form onSubmit={form.handleSubmit}>
                <FormLoader loading={form.isSubmitting} />
                <h1 className='expert-form__title'>{title}</h1>
                <FormError error={this.state.error} />

                <InvoiceOverpaidNotification
                  invoice={invoice}
                  invoicePayments={form.values.invoicePayments}
                />

                <InvoicePayments
                  form={form}
                  label={`Please add payment date, status, amount (${currencyName})`}
                  name='invoicePayments'
                  staticData={staticData}
                  onInvoicePaymentStatusChange={
                    this.handleInvoicePaymentStatusChange
                  }
                />

                <div className='expert-form__section p-b-0'>
                  <Button primary submit disabled={form.isSubmitting}>
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )
        }}
      />
    )
  }
}

const InvoicePayments = ({
  form,
  label,
  name,
  staticData,
  onInvoicePaymentStatusChange
}) => {
  const invoicePayments = form.values.invoicePayments || []
  const today = moment().format('YYYY-MM-DD')
  return (
    <div className='' data-cy='invoice-payment-modal'>
      <label className=''>{label}</label>
      <div className='expert-form__input-wrapper'>
        <FieldArray
          name={name}
          render={arrayHelpers => (
            <div className='expert-form__repeatable-input-wrapper'>
              {invoicePayments.map((invoiceItem, index) => {
                const invoicePaymentStatusName = `${name}[${index}].invoicePaymentStatus`

                return (
                  <FlexRow key={index} itemSpacing='lg' className='m-b-md'>
                    <FlexItem>
                      <Field
                        name={`${name}[${index}].paidDate`}
                        placeholder=''
                        dateOnly
                        component={DatePicker}
                        value={name[index].paidDate}
                        maxDate={today}
                      />
                    </FlexItem>
                    <FlexItem>
                      <Field
                        name={`${name}[${index}].amount`}
                        placeholder=''
                        component={Input}
                        value={name[index].amount}
                      />
                    </FlexItem>
                    <FlexItem flexContent>
                      <Field
                        data-cy='invoice-payment-type-dropdown'
                        name={invoicePaymentStatusName}
                        label=''
                        placeholder=''
                        itemToString={item => item && item.name}
                        options={staticData.invoicePaymentStatuses}
                        onChange={invoicePaymentStatus =>
                          onInvoicePaymentStatusChange(
                            form,
                            invoicePaymentStatusName,
                            invoicePaymentStatus
                          )
                        }
                        component={Dropdown}
                      />

                      <RepeatableAction
                        fieldValues={invoicePayments}
                        allowDelete
                        arrayHelpers={arrayHelpers}
                        index={index}
                        initialObject={generateEmptyInvoicePayment(staticData)}
                      />
                    </FlexItem>
                  </FlexRow>
                )
              })}
            </div>
          )}
        />
      </div>
    </div>
  )
}

InvoicePayments.propTypes = {
  form: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  staticData: PropTypes.object.isRequired,
  onInvoicePaymentStatusChange: PropTypes.func.isRequired,
  label: PropTypes.string
}

function generateEmptyInvoicePayment (staticData) {
  const { invoicePaymentStatuses } = staticData
  const { INVOICE_PAYMENT_STATUSES } = staticData.CONST
  const receivedStatus = invoicePaymentStatuses.find(
    status => status.id === INVOICE_PAYMENT_STATUSES.RECEIVED
  )

  const emptyInvoicePayment = {
    paidDate: null,
    amount: null,
    invoicePaymentStatusId: INVOICE_PAYMENT_STATUSES.RECEIVED,
    invoicePaymentStatus: receivedStatus
  }

  return emptyInvoicePayment
}

function initializeForm ({ invoice, staticData }) {
  const emptyInvoicePayments = [generateEmptyInvoicePayment(staticData)]

  if (!invoice.id) {
    return {
      invoicePayments: emptyInvoicePayments
    }
  }

  const invoicePayments = invoice.invoicePayments.length
    ? toJS(invoice.invoicePayments)
    : emptyInvoicePayments

  return {
    invoicePayments: invoicePayments
  }
}

function getValidationSchema ({ staticData }) {
  const { RECEIVED } = staticData.CONST.INVOICE_PAYMENT_STATUSES
  return yup.object().shape({
    invoicePayments: yup.array().of(
      yup.object().shape({
        paidDate: yup
          .string()
          .required('Paid date is required')
          .typeError('Paid date is required'),
        amount: moneyValidate(),
        invoicePaymentStatus: yup
          .object()
          .shape({
            id: yup
              .number()
              .default(RECEIVED)
              .typeError('Payment status is required')
              .required('Payment status is required')
          })
          .typeError('Payment status is required')
      })
    )
  })
}

export default inject(
  'invoiceStore',
  'staticData',
  'modalStore',
  'growlStore',
  'contractStore',
  'deliverableStore'
)(observer(InvoicePaymentForm))
